import * as React from 'react';
import { useState, ChangeEvent, FocusEvent, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from 'react-intl';
import TagManager from 'react-gtm-module';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Typography, Box, TextField, Button, Menu, MenuItem, Dialog, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import isEmail from 'validator/lib/isEmail';
import Checkbox from './Checkbox';
import * as FitterHelper from '../library/FitterHelper';
import * as IosHelper from '../library/IosHelper';
import Golfer from '../classes/Golfer';
import Fitter from '../classes/Fitter';
import './GolferInfo.css';
interface IMenuProps {
    id: string;
    MenuListProps: any;
    anchorEl: any;
    open: any;
    onClose: any;
}

const StyledMenu = styled((props: IMenuProps) => (
    <Menu
        elevation={0}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
        }}
        {...props}
    />
))(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 6,
        marginTop: theme.spacing(1),
        minWidth: 180,
        color:
            theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
        boxShadow:
            'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
            padding: '4px 0',
        },
        '& .MuiMenuItem-root': {
            '& .MuiSvgIcon-root': {
                fontSize: 18,
                color: theme.palette.text.secondary,
                marginRight: theme.spacing(1.5),
            },
            '&:active': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    theme.palette.action.selectedOpacity,
                ),
            },
        },
    },
}));

export default function GolferMenu(props: any) {
    let propsGolfer = props.golfer as Golfer;
    const cbGolferUpdate = props.cbgolferupdate; //callback to tell main form that the golfer was updated
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editGolfer, setEditGolfer] = useState(new Golfer());
    const [nameIsValid, setNameIsValid] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [emailIsValid, setEmailIsValid] = useState(true);
    const [fitter, setFitter] = useState(new Fitter());
    const [phoneIsValid, setPhoneIsValid] = useState(false);
    const [iOS, setiOS] = useState(false);
    const showSMSOptions = fitter.country === "US" && !iOS;

    const intl = useIntl();
    const intlEnterName = intl.formatMessage({ id: "general.enter.name" });
    const intlEmailInvalid = intl.formatMessage({ id: "general.email.invalid" });
    const intlPhoneInvalid = intl.formatMessage({ id: "general.phone.invalid" });

    useEffect(() => {
        (async () => {
            setFitter(await FitterHelper.getFitter());
            setiOS(await IosHelper.get());
        })();
    }, [])

    useEffect(() => {
        if (!propsGolfer) return;
        setEditGolfer({ ...propsGolfer });
    }, [propsGolfer])

    const open = Boolean(anchorEl);
    const handleFitterButton = (event: any) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    let navigate = useNavigate();

    const handleGolferInfoButton = () => {
        TagManager.dataLayer({
            dataLayer: {
                event: 'vwf_edit_golfer_info'
            }
        })
        setAnchorEl(null);
        setDialogOpen(true);
    }

    const handleSaveQuitButton = () => {
        TagManager.dataLayer({
            dataLayer: {
                event: 'vwf_save_and_quit'
            }
        })
        setAnchorEl(null);
        navigate('/apps/wedgefitting/golferinfo');
    }

    const handleDialogCancel = () => {
        setEditGolfer({ ...propsGolfer });
        setDialogOpen(false);
    }

    const handleDialogSave = async () => {
        let isValid = validatePage(editGolfer);
        setShowErrors(!isValid);
        //if email is empty, then prompt if wants to enter their email or not
        if (isValid) {
            propsGolfer = editGolfer;
            cbGolferUpdate(editGolfer);
            setDialogOpen(false);
        }
    }

    const handleSMSChange = () => {
        const newGolfer = { ...editGolfer, smsOptIn: !editGolfer.smsOptIn };
        setEditGolfer(newGolfer);
    }

    const handleFormChange = ({ target }: ChangeEvent<HTMLInputElement>): void => {
        let newGolfer = { ...editGolfer, [target.name]: target.value } as Golfer;
        newGolfer.fullName = (newGolfer.firstName + " " + newGolfer.lastName).trim();
        validatePage(newGolfer); //refresh form error messages if necessary
        setEditGolfer(newGolfer);
    }

    const handleEmailBlur = ({ target }: FocusEvent<HTMLInputElement>): void => {
        validateEmail(target.value);
    }

    const validateEmail = (email: string): boolean => {
        const validEmail = (email === "") || (email !== "" && isEmail(email));
        setEmailIsValid(validEmail)
        return validEmail;
    }

    const validatePage = (golfer: Golfer): boolean => {
        //email is not required
        let validEmail = true;
        validEmail = validateEmail(golfer.email);

        let validPhoneNumber = !golfer.smsOptIn || (golfer.smsOptIn && golfer.phone != null && golfer.phone !== "");
        setPhoneIsValid(validPhoneNumber);

        const validaName = golfer.firstName.trim() != "" && golfer.lastName.trim() !== "";
        setNameIsValid(validaName);

        let isValid = validaName && validEmail && validPhoneNumber;
        return isValid;
    }

    return (
        <>
            <Button type="submit" disableRipple className="golfer-info-button" onClick={handleFitterButton} endIcon={<KeyboardArrowDownIcon />}>{propsGolfer.fullName}</Button>
            <StyledMenu
                id="demo-customized-menu"
                MenuListProps={{
                    'aria-labelledby': 'demo-customized-button',
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
            >
                <MenuItem onClick={handleGolferInfoButton} disableRipple>
                    <FormattedMessage id="golfer.menu.edit.golfer.info" />
                </MenuItem>
                <MenuItem onClick={handleSaveQuitButton} disableRipple>
                    <FormattedMessage id="golfer.menu.save.quit" />
                </MenuItem>
            </StyledMenu>


            <Dialog
                open={dialogOpen}
                onClose={handleDialogCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogContent>
                    <DialogContentText id="alert-dialog-description" sx={{ color: 'black' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}
                        >
                            <Typography variant="h5" className='header' sx={{ color: 'black !important' }}>
                                <FormattedMessage id="general.appname" />
                            </Typography>
                            <Box sx={{ mt: 1, width: '500px' }}>
                                <div style={{ width: "100%", display: "flex", gap: "20px" }}>
                                    <div style={{ width: "100%" }}>
                                        <Typography className='label' sx={{ color: 'black !important' }}>
                                            <FormattedMessage id="general.golfer.first.name" />
                                        </Typography>
                                        <TextField
                                            value={editGolfer.firstName}
                                            margin="normal"
                                            required
                                            fullWidth
                                            id="firstName"
                                            name="firstName"
                                            onChange={handleFormChange}
                                            inputProps={{ maxLength: 50 }}
                                        />

                                    </div>
                                    <div style={{ width: "100%" }}>
                                        <Typography className='label' sx={{ color: 'black !important' }}>
                                            <FormattedMessage id="general.golfer.last.name" />
                                        </Typography>
                                        <TextField
                                            value={editGolfer.lastName}
                                            margin="normal"
                                            required
                                            fullWidth
                                            id="lastName"
                                            name="lastName"
                                            onChange={handleFormChange}
                                            inputProps={{ maxLength: 50 }}
                                        />

                                    </div>
                                </div>
                                <div style={{ width: "100%", display: "flex", gap: "20px" }}>
                                    <div style={{ width: "100%" }}>
                                        <Typography className='label' sx={{ color: 'black !important' }}>
                                            <FormattedMessage id="general.golfer.email" />
                                        </Typography>
                                        <TextField
                                            margin="normal"
                                            required
                                            fullWidth
                                            id="email"
                                            name="email"
                                            value={editGolfer.email}
                                            onChange={handleFormChange}
                                            onBlur={handleEmailBlur}
                                            sx={{ marginBottom: '10px !important' }}
                                            inputProps={{ maxLength: 200 }}
                                        />
                                    </div>
                                </div>
                                {showErrors &&
                                    <>
                                        {!nameIsValid &&
                                            <Typography className='label' sx={{ color: 'red !important', marginBottom: '15px', minHeight: '24px' }}>
                                                {intlEnterName}
                                            </Typography>
                                        }
                                        {!emailIsValid &&
                                            <Typography className='label' sx={{ color: 'red !important', marginBottom: '15px', minHeight: '24px' }}>
                                                {intlEmailInvalid}
                                            </Typography>
                                        }
                                        {!phoneIsValid &&
                                            <Typography className='label' sx={{ color: 'red !important', marginBottom: '15px', minHeight: '24px' }}>
                                                {intlPhoneInvalid}
                                            </Typography>
                                        }
                                    </>
                                }
                                {showSMSOptions &&
                                    <>
                                        <div style={{ width: "100%" }}>
                                            <Typography className='label' sx={{ color: 'black !important' }}>
                                                <FormattedMessage id="general.golfer.phone" />
                                            </Typography>
                                            <div style={{ display: "flex" }}>
                                                <label style={{ marginTop: "35px", marginRight: "10px" }}>+1</label>
                                                <TextField
                                                    margin="normal"
                                                    required
                                                    fullWidth
                                                    id="phone"
                                                    name="phone"
                                                    value={editGolfer.phone}
                                                    onChange={handleFormChange}
                                                    onBlur={handleEmailBlur}
                                                    sx={{ marginBottom: '10px !important' }}
                                                    inputProps={{ maxLength: 200 }}
                                                />
                                            </div>
                                        </div>
                                        <Checkbox checked={editGolfer.smsOptIn || false} onChange={handleSMSChange}>
                                            <div>
                                                <b><FormattedMessage id="general.sms.updates" /></b>&nbsp;
                                                <span className="golfer-registration--sms-message"><FormattedMessage id="general.sms.message" /></span>
                                            </div>
                                        </Checkbox>
                                        <div className="golfer-registration--sms-notice">
                                            <FormattedMessage id="general.sms.notice.1" />&nbsp;
                                            <FormattedMessage id="general.sms.notice.2" />&nbsp;
                                            <span style={{ color: "red" }}><FormattedMessage id="general.privacy.policy" /></span>&nbsp;
                                            <FormattedMessage id="general.and"></FormattedMessage>&nbsp;
                                            <span style={{ color: "red" }}><FormattedMessage id="general.sms.terms.and.conditions" /></span>&nbsp;
                                            <FormattedMessage id="general.sms.notice.3" />
                                        </div>
                                    </>}
                            </Box>
                        </Box>
                    </DialogContentText>
                </DialogContent>
                <DialogActions className='appbar'>
                    <Button disableRipple variant='outlined' className='cancel-button' onClick={handleDialogCancel} sx={{ color: 'black', borderColor: 'black' }}><FormattedMessage id="general.cancel" /></Button>
                    <Button disableRipple variant='outlined' className='submit-button' onClick={handleDialogSave} sx={{ color: 'black', borderColor: 'black' }} ><FormattedMessage id="general.save" /></Button>
                </DialogActions>
            </Dialog>

        </>
    )
}